import React from "react";
import { useMutation, useNotify, useRedirect, useUnselectAll } from "react-admin";

import { OrderService } from "../entities/OrderService";
import { useNotifyError } from "../utils/notifiers/useNotifyError";
import { getFieldErrors } from "./getValidationErrors";

export const useMutateForm = (
	type,
	resource,
	basePath,
	id,
	options,
	handleCustomRedirect = undefined,
	extendPayload = undefined,
	successMessage = undefined
) => {
	const notify = useNotify();
	const notifyError = useNotifyError();
	const redirect = useRedirect();
	const unselectAll = useUnselectAll();

	const updateManyTypes = ["updateMany", "updateManyArray", "updateManyPartialArray"];
	const payload = updateManyTypes.includes(type) ? { ids: id } : { id };

	const [mutate, state] = useMutation(
		{ type, resource, payload },
		{
			...options,
			returnPromise: true,
		}
	);

	const msgSuccess = successMessage || (
		type === "create"
			? "ra.notification.created"
			: ["update", ...updateManyTypes].includes(type)
			? "ra.notification.updated"
			: "Done"
	);

	const mutateForm = React.useCallback(
		async (values, redirectTo) => {
			try {
				const data =
					typeof extendPayload === "function"
						? extendPayload({ ...values }, { type, resource, id }) || { ...values }
						: { ...values };

				const mutationData = { payload: { data } };

				await mutate(mutationData, {
					onSuccess: (response) => {
						const record = type === "create" ? response.data : values;

						const customHandlerDidWork =
							typeof handleCustomRedirect === "function" && handleCustomRedirect(record);

						if (!customHandlerDidWork) {
							if (updateManyTypes.includes(type)) {
								unselectAll(resource);
								redirect(redirectTo, basePath);
							} else {
								redirect(redirectTo, basePath, record?.id, record);
							}
						}
						notify(msgSuccess, "info", { smart_count: 1 });
					},
				});
			} catch (error) {
				const fieldErrors = getFieldErrors(error);
				if (Object.keys(fieldErrors).length > 0) {
					notify("ra.notification.bad_item", "error");
					return fieldErrors;
				} else {
					notifyError(error);
				}
			}
		},
		[mutate, extendPayload]
	);

	return [mutateForm, state];
};
